import { useCallback, useMemo, useState } from "react";
import { LocationValidator } from "../../../../../infrastructure/location-validator/location-validator";

const centerCoodinates = {
  longitude: 139.72919522664367,
  latitude: 35.6607217363482,
};

const radius = 1; // in kilo meters
const bypassKey = import.meta.env.VITE_GPS_VALIDATION_BYPASS_KEY;
const chanelLinePageURL = import.meta.env.VITE_CHANEL_LINE_URL;
const disableGpsValidation =
  import.meta.env.VITE_DISABLE_GPS_VALIDATION === "true";

type GPSStatus = "initial" | "requesting" | "granted" | "denied" | "error";
type ValidationStatus = "idle" | "validating" | "success" | "failed";
type ButtonType = "line" | "non-line" | null;

export const useChanelLandingPage = () => {
  const [gpsStatus, setGpsStatus] = useState<GPSStatus>("initial");
  const [validationStatus, setValidationStatus] =
    useState<ValidationStatus>("idle");
  const [error, setError] = useState<string>("");

  // クエリ文字列に特殊なトークンが含まれているかチェック（仮実装）
  const hasSpecialToken = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("foHhXnRFt3Gi");
    return token === bypassKey;
  }, []);

  const hasButtons = useMemo(() => {
    const currentParams = new URLSearchParams(window.location.search);
    const query = currentParams.get("hasButtons");
    return query !== null && query === "true";
  }, []);

  // /entryページに遷移
  const navigateToEntry = useCallback(() => {
    const currentParams = new URLSearchParams(window.location.search);
    window.location.href = `/entry?${currentParams.toString()}`;
  }, []);

  // LINEリダイレクト
  const redirectToLine = useCallback(() => {
    window.location.href = chanelLinePageURL;
  }, []);

  const handleLocationValidation = useCallback(
    async (buttonType: ButtonType) => {
      if (disableGpsValidation) {
        setValidationStatus("success");
        // ボタンによって遷移先を分ける
        if (buttonType === "line") {
          redirectToLine();
        } else if (buttonType === "non-line") {
          navigateToEntry();
        }
        return;
      }

      setValidationStatus("validating");

      try {
        const locationValidator = new LocationValidator();

        // 特殊なトークンがある場合はバリデーションをスキップ
        locationValidator.initialize(hasSpecialToken());

        const isValid = await locationValidator.validateAsync(
          centerCoodinates,
          radius
        );

        if (isValid) {
          setValidationStatus("success");

          // ボタンによって遷移先を分ける
          if (buttonType === "line") {
            redirectToLine();
          } else if (buttonType === "non-line") {
            navigateToEntry();
          }
        } else {
          setValidationStatus("failed");
        }
      } catch (error) {
        setValidationStatus("failed");
        setError("位置情報の検証中にエラーが発生しました");
      }
    },
    [hasSpecialToken, redirectToLine, navigateToEntry]
  );

  const handleRequestGPS = useCallback(
    async (buttonType: ButtonType) => {
      if (disableGpsValidation) {
        setGpsStatus("granted");
        handleLocationValidation(buttonType);
        return;
      }

      setGpsStatus("requesting");
      setError("");

      try {
        // GPS権限の要求
        const result = await navigator.permissions.query({
          name: "geolocation",
        });

        if (result.state === "granted") {
          setGpsStatus("granted");
          handleLocationValidation(buttonType);
        } else if (result.state === "denied") {
          setGpsStatus("denied");
        } else {
          // prompt状態の場合、実際に位置情報を取得して権限を確認
          try {
            await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                timeout: 10000,
                enableHighAccuracy: true,
              });
            });
            setGpsStatus("granted");
            handleLocationValidation(buttonType);
          } catch (positionError) {
            setGpsStatus("denied");
          }
        }
      } catch (error) {
        setGpsStatus("error");
        setError("GPS権限の確認中にエラーが発生しました");
      }
    },
    [handleLocationValidation]
  );

  const handleButtonClick = useCallback(
    (buttonType: ButtonType) => {
      handleRequestGPS(buttonType);
    },
    [handleRequestGPS]
  );

  return {
    gpsStatus,
    validationStatus,
    error,
    hasButtons,
    handleButtonClick,
  };
};
